/**
 * `LakikaveriStepsList` — the four numbered collapsible steps for the
 * `lakikaveri-info` article (TA20.2).
 *
 * Why a separate component
 * ========================
 *
 * Two reasons:
 *
 *   (a) FR-045 file-size pressure. The `LakikaveriInfoRoute` is the
 *       largest of all 5 bespoke routes; extracting the steps list
 *       (the per-step rendering of `bodyHtml` + actions + documents +
 *       internal links) keeps the route file well under the 400-line
 *       ceiling.
 *   (b) Hook colocation. The contact trigger only fires from within a
 *       step's actions array, so the wizard handle it dispatches to
 *       belongs next to the rendering site.
 *
 * Per-step composition
 * ====================
 *
 * Each step renders as a `<details>` with:
 *
 *   1. `<summary>` showing `${step.number}. ${step.titleFi}`. The
 *      stable `step.number` field doubles as the React key (Phase A
 *      seeds 1..4 in order; the inventory pins this; if a future
 *      seed ever introduces gaps or duplicates the type-guard would
 *      not detect that — but the rendered output's numbering would
 *      look obviously wrong).
 *   2. The step's `bodyHtml` via `SanitizedHtml` (FR-049). The
 *      sanitised wrapper is the only sanctioned route for arbitrary
 *      HTML — the architecture check enforces it.
 *   3. If `step.actions` includes `'openContactModal'`: a
 *      `<button type="button">` that opens the compose wizard — the
 *      app's single "Ota yhteyttä" experience (027 US6 / D-120).
 *   4. If `step.documents` is non-empty: a `<ul>` of external
 *      `<a target="_blank" rel="noopener noreferrer">` links.
 *   5. If `step.internalLinks` is non-empty: a `<ul>` of
 *      react-router `<Link>` elements (intra-app navigation, not
 *      `target="_blank"`).
 *
 * A11y posture
 * ============
 *
 *   - Native `<details>`/`<summary>` provides keyboard-accessible
 *     expand/collapse (Enter, Space) and focus management for free.
 *   - The contact trigger is a plain `<button type="button">`,
 *     not an anchor or a styled-`<div>` — the button role is correct
 *     for an in-page state-toggle.
 *   - The internal-link `<ul>` uses real `<Link>` (anchor under the
 *     hood), so AT users get the "link" role with the correct
 *     destination announced.
 *
 * Spec references: same as `LakikaveriInfoRoute.tsx` (TA20.2).
 */

import type { LakikaveriStep } from '@omaplus/shared-types/content-metadata';
import type { JSX } from 'react';
import { Link } from 'react-router';

import { useComposeWizard } from '../../member-messaging/compose/wizard/ComposeWizardProvider.js';
import { SanitizedHtml } from '../components/SanitizedHtml.js';

const stepsListClasses = 'flex flex-col gap-2';
const stepSummaryClasses = 'cursor-pointer select-none text-base font-medium text-foreground';
const stepBodyClasses = 'flex flex-col gap-3 pt-2 text-sm text-foreground';
const stepProseClasses = 'flex flex-col gap-2 text-sm text-foreground';
const stepActionButtonClasses =
  'self-start inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90';
const stepDocumentsListClasses = 'm-0 flex flex-col gap-1 pl-5 text-sm';
const stepInternalLinksListClasses = 'm-0 flex flex-col gap-1 pl-5 text-sm';
const stepLinkClasses = 'text-primary underline';

/**
 * Route-specific Finnish label for the contact trigger button.
 * Co-located with the route per `copy.ts`'s file-header policy.
 */
const CONTACT_WIZARD_TRIGGER_COPY: string = 'Ota yhteyttä';

export interface LakikaveriStepsListProps {
  readonly steps: ReadonlyArray<LakikaveriStep>;
  readonly contactLabel?: string | undefined;
}

/**
 * Renders the numbered collapsible steps. Each step's `openContactModal`
 * action opens the app's single "Ota yhteyttä" experience — the compose
 * wizard (027 US6 / D-120, FR-023) — replacing the interim placeholder
 * `ContactModal` this route used to own. The seed's `'openContactModal'`
 * discriminator value stays verbatim as a compatibility shim (renaming it
 * is a content-authoring decision, not this component's).
 */
export function LakikaveriStepsList({
  steps,
  contactLabel,
}: LakikaveriStepsListProps): JSX.Element {
  const composeWizard = useComposeWizard();

  return (
    <section className={stepsListClasses} aria-label="Lakikaveri-vaiheet">
      {steps.map((step) => (
        <details key={step.number}>
          <summary className={stepSummaryClasses}>
            {step.number}. {step.titleFi}
          </summary>
          <div className={stepBodyClasses}>
            <SanitizedHtml as="div" className={stepProseClasses} html={step.bodyHtml} />
            {step.actions?.includes('openContactModal') ? (
              <button
                type="button"
                className={stepActionButtonClasses}
                onClick={() => composeWizard.open()}
              >
                {contactLabel ?? CONTACT_WIZARD_TRIGGER_COPY}
              </button>
            ) : null}
            {step.documents !== undefined && step.documents.length > 0 ? (
              <ul className={stepDocumentsListClasses}>
                {step.documents.map((document) => (
                  <li key={document.externalUrl}>
                    <a
                      className={stepLinkClasses}
                      href={document.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {document.titleFi}
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
            {step.internalLinks !== undefined && step.internalLinks.length > 0 ? (
              <ul className={stepInternalLinksListClasses}>
                {step.internalLinks.map((link) => (
                  <li key={link.path}>
                    <Link className={stepLinkClasses} to={link.path}>
                      {link.labelFi}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </details>
      ))}
    </section>
  );
}
